import * as v from 'valibot';

/**
 * Redaction strategies applied to a column or a matched value once the policy
 * engine decides it must not reach the model.
 *
 * - `drop` removes the key entirely.
 * - `mask` keeps the shape but replaces characters (`ab***@***.com`).
 * - `hash` substitutes a stable, non-reversible token so the model can still
 *   correlate rows without seeing the value.
 * - `null` keeps the key with a null value, which keeps result shapes stable
 *   for code-mode programs that index into rows positionally.
 */
export const RedactionMode = v.picklist(['drop', 'mask', 'hash', 'null']);
export type RedactionMode = v.InferOutput<typeof RedactionMode>;

export const PiiClass = v.picklist([
  'email',
  'phone',
  'ssn',
  'credit_card',
  'ip_address',
  'name',
  'address',
  'secret',
]);
export type PiiClass = v.InferOutput<typeof PiiClass>;

/**
 * A deny pattern is matched against *serialized tool output* before the result
 * is handed back to the model. `appliesTo` narrows the match so a pattern for
 * SQL text does not accidentally scan a 10k-row result body.
 */
export const DenyPattern = v.object({
  id: v.string(),
  /** JS regular expression source, matched case-insensitively unless `flags` says otherwise. */
  pattern: v.string(),
  flags: v.optional(v.string(), 'i'),
  appliesTo: v.optional(v.picklist(['input', 'output', 'both']), 'both'),
  /** `block` fails the call; `redact` replaces every match with `replacement`. */
  action: v.optional(v.picklist(['block', 'redact']), 'block'),
  replacement: v.optional(v.string(), '[redacted]'),
  reason: v.optional(v.string()),
});
export type DenyPattern = v.InferOutput<typeof DenyPattern>;

/**
 * Table-scoped rules. A policy without a `tables` entry for a queried table
 * falls back to the policy defaults; a policy with `tableAllowlist` set rejects
 * any table outside it outright.
 */
export const TableRule = v.object({
  table: v.string(),
  maxRows: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0))),
  allowedColumns: v.optional(v.array(v.string())),
  deniedColumns: v.optional(v.array(v.string())),
  /** Rows the caller may never see, expressed as a SQL-ish predicate for humans and a matcher for us. */
  rowFilter: v.optional(v.string()),
});
export type TableRule = v.InferOutput<typeof TableRule>;

export const PolicySpec = v.object({
  /** Stable id — `policies/<id>.json` in the config repo. */
  id: v.string(),
  version: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1)), 1),
  description: v.optional(v.string()),

  /**
   * Hard ceiling on rows returned to the model from any single tool call.
   * The engine truncates rather than failing, and records the truncation in the
   * audit trail plus a note the model can see, so it knows the result is partial.
   */
  maxRows: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 100),
  /** Ceiling on serialized result bytes, applied after row truncation. */
  maxBytes: v.optional(v.pipe(v.number(), v.integer(), v.minValue(256)), 128 * 1024),

  /** Global column allowlist. When present, every returned object is projected onto it. */
  allowedColumns: v.optional(v.array(v.string())),
  /** Global column denylist, applied after `allowedColumns`. */
  deniedColumns: v.optional(v.array(v.string())),

  /** When set, a query touching a table outside this list is blocked before execution. */
  tableAllowlist: v.optional(v.array(v.string())),
  tables: v.optional(v.array(TableRule), []),

  denyPatterns: v.optional(v.array(DenyPattern), []),

  /** PII classes to detect heuristically in values and redact. */
  redactPii: v.optional(v.array(PiiClass), []),
  redactionMode: v.optional(RedactionMode, 'mask'),

  /** Reject writes outright. Convenience over spelling out deny patterns. */
  readOnly: v.optional(v.boolean(), false),
});
export type PolicySpec = v.InferOutput<typeof PolicySpec>;

/** A policy as authored in `/policies/<id>.json`, before defaults are filled in. */
export const PolicySpecInput = v.partial(PolicySpec, ['version']);
export type PolicySpecInput = v.InferInput<typeof PolicySpec>;

export const EMPTY_POLICY: PolicySpec = v.parse(PolicySpec, { id: 'unrestricted', maxRows: 100 });

export function parsePolicy(input: unknown): PolicySpec {
  return v.parse(PolicySpec, input);
}
